import { sortBy } from 'lodash';
import moment, { Moment } from 'moment-timezone';

export const TIMEZONES = [
  'US/Alaska',
  'US/Arizona',
  'US/Central',
  'US/Eastern',
  'US/East-Indiana',
  'US/Hawaii',
  'US/Mountain',
  'US/Pacific',
  'US/Samoa'
] as const;

export const TIME_FORMATS = {
  DATE: 'MM/DD/YYYY',
  TIME: 'h:mma',
  THIS_YEAR: 'MMM D YYYY',
  LAST_YEAR: 'MMM D',
  FORMAL_DATE: 'YYYY-MM-DD',
  TIME_TZ: 'h:mma zz'
};

export const GetCurrentTZ = (): string => {
  let tz: string = moment.tz.guess();
  // Attempt to resolve more accurately
  const summer = new Date(Date.UTC(2005, 6, 30, 0, 0, 0, 0));
  const so = -1 * summer.getTimezoneOffset();
  const winter = new Date(Date.UTC(2005, 12, 30, 0, 0, 0, 0));
  const wo = -1 * winter.getTimezoneOffset();
  if (so === -480 && wo === -540) {
    tz = 'US/Alaska';
  } else if (so === -420 && wo === -420) {
    tz = 'US/Arizona';
  } else if (so === -300 && wo === -360) {
    tz = 'US/Central';
  } else if (so === -240 && wo === -300) {
    tz = 'US/Eastern';
  } else if (so === -300 && wo === -300) {
    tz = 'US/East-Indiana';
  } else if (so === -600 && wo === -600) {
    tz = 'US/Hawaii';
  } else if (so === -360 && wo === -420) {
    tz = 'US/Mountain';
  } else if (so === -420 && wo === -480) {
    tz = 'US/Pacific';
  } else if (so === -660 && wo === -660) {
    tz = 'US/Samoa';
  }
  return tz;
};

export const WEEKDAYS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday'
];

export const WEEKDAYS_ABBR = WEEKDAYS.map((day) => day.substring(0, 3));

export const WEEKDAYS_SHORT = ['Su', 'M', 'Tu', 'W', 'Th', 'F', 'Sa'];

// Date format functions

export const FormatDate = (
  date: Moment | Date,
  format?: string,
  tz?: string
): string => {
  if (!date) {
    return '';
  }
  tz = tz || GetCurrentTZ();
  if (!moment.isMoment(date)) {
    date = moment.tz(date, tz);
  }
  format = format || TIME_FORMATS.DATE;
  return date.format(format);
};

export const FormatTime = (
  time: Moment | Date,
  format?: string,
  tz?: string
): string => {
  if (!time) {
    return '';
  }
  tz = tz || GetCurrentTZ();
  if (!moment.isMoment(time)) {
    time = moment.tz(time, tz);
  }
  format = format || TIME_FORMATS.TIME;
  return time.format(format);
};

export const FormatDateTime = (
  datetime: Moment | Date,
  format?: string,
  tz?: string
): string => {
  return `${FormatDate(datetime, format, tz)} ${FormatTime(
    datetime,
    format,
    tz
  )}`;
};

export interface DaysAndClassesOutput {
  className: string;
  date: string;
  days: number;
  naturalDate: string;
  time: string;
}

export const GetStartOfDayDelta = (earlier: Moment, later: Moment): number => {
  const a = later.clone().startOf('day');
  const b = earlier.clone().startOf('day');
  return a.diff(b, 'days');
};

// NOTE: consider refactoring to use moment.fromNow(), it can be customized with updateLocale()
export const FormatDaysAndClasses = (
  date: Moment,
  tz?: string,
  isProspective?: boolean
): DaysAndClassesOutput => {
  tz = tz || GetCurrentTZ();
  const currDate = moment.tz(date, tz);
  const now = moment.tz(tz);
  const days = Math.abs(GetStartOfDayDelta(currDate, now));
  const formattedDate = currDate.format(
    now.year() !== currDate.year()
      ? TIME_FORMATS.LAST_YEAR
      : TIME_FORMATS.THIS_YEAR
  );
  const formattedTime = currDate.format(TIME_FORMATS.TIME_TZ);
  const modifier = isProspective ? 'Left' : 'Ago';
  let className = 'bg-blue text-white';
  let naturalDate = 'Today';
  if (days === 0 && isProspective) {
    const hours = Math.abs(now.diff(currDate, 'hours'));
    if (hours === 1) {
      naturalDate = `${hours} Hour ${modifier}`;
    } else if (hours > 1) {
      naturalDate = `${hours} Hours ${modifier}`;
    } else {
      naturalDate = `${Math.abs(
        now.diff(currDate, 'minutes')
      )} Mins ${modifier}`;
    }
  } else if (days === 1) {
    className = 'bg-light-blue text-gray';
    naturalDate = isProspective ? 'Tomorrow' : 'Yesterday';
  } else if (days === 2) {
    className = 'bg-light-blue text-gray';
    naturalDate = `2 days ${modifier}`;
  } else if (days >= 3) {
    className = 'bg-light-blue text-gray';
    naturalDate = `${days} Days ${modifier}`;
  } else if (days >= 14) {
    className = 'bg-light-gray text-gray';
    naturalDate = `${days} Days ${modifier}`;
  }
  return {
    className,
    date: formattedDate,
    days,
    naturalDate,
    time: formattedTime
  };
};

export const FormatDaysAndClassesLeft = (
  date: Moment | Date,
  tz?: string
): DaysAndClassesOutput => {
  if (date instanceof Date) {
    date = moment(date);
  }
  return FormatDaysAndClasses(date, tz, true);
};

export const FormatDaysAndClassesAgo = (
  date: Moment | Date,
  tz?: string
): DaysAndClassesOutput => {
  if (date instanceof Date) {
    date = moment(date);
  }
  return FormatDaysAndClasses(date, tz, false);
};

export const GetWeekdaysInterval = (days: number[]): string => {
  days = sortBy(days);
  const parts: [number, number][] = [];
  let start: number = null;
  let end: number = null;
  let prev: number = null;
  for (const [i, day] of days.entries()) {
    // Start of the marker
    if (prev == null) {
      prev = day;
      start = day;
      end = day;
      continue;
    }
    // When the day is no longer continuous, output & reset
    if (prev + 1 !== day) {
      parts.push([start, end]);
      if (i === days.length - 1) {
        parts.push([day, day]);
      }
      prev = day;
      start = day;
      end = day;
    }
    end = day;
    prev = day;
    // End of the day array, output
    if (i === days.length - 1) {
      parts.push([start, end]);
    }
  }
  let interval = '';
  for (const [i, part] of parts.entries()) {
    if (part[0] === part[1]) {
      interval += WEEKDAYS_SHORT[part[0]];
    } else {
      interval += WEEKDAYS_SHORT[part[0]] + '-' + WEEKDAYS_SHORT[part[1]];
    }
    if (i !== parts.length - 1) {
      interval += ',';
    }
  }
  if (interval === 'M-F') {
    interval = 'Weekdays';
  } else if (interval === 'Su,Sa') {
    interval = 'Weekends';
  } else if (interval === 'Su-Sa') {
    interval = 'Everyday';
  }
  return interval;
};

export enum ShorthandDate {
  Yesterday = -1,
  Today = 0,
  Tomorrow = 1,
  ThreeMonths = 90
}

export const GetFormalDate = (delta: ShorthandDate): string => {
  return moment().add(delta, 'days').format(TIME_FORMATS.FORMAL_DATE);
};
